import {CreateIssueDialogEntryQuery} from '@github-ui/server-persisted-queries'
import type {CreateIssueDialogEntryQueryQuery} from '@github-ui/server-persisted-queries/types/CreateIssueDialogEntryQuery'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'

// PENDING_BACKEND_SYNC(CreateIssueDialogEntryQuery): the generated SPQ export/manifest for this
// operation has been synced from github/github#443693 and is pushed ahead of the backend merge
// (coordinator-approved). Only the merged-SHA verification (`verify-single-query-syncs`) stays
// red until #443693 merges; nothing else is held.

type DialogEntryResponse = {
  data?: CreateIssueDialogEntryQueryQuery
  errors?: readonly unknown[]
}

// The dialog-entry repository fetch fires once when the create-issue dialog opens for a
// preselected repo. Cache it briefly so a StrictMode double-invoke (or a quick close/reopen)
// dedupes to a single request while still refetching after the window expires.
export const DIALOG_ENTRY_STALE_TIME = 5 * 60 * 1000

export function dialogEntryQueryKey(owner: string, repo: string) {
  return ['issue-create', 'dialog-entry', {owner, repo}] as const
}

/**
 * Fetches the dialog's target repository fields from the server-persisted
 * `CreateIssueDialogEntryQuery` Rails endpoint. The backend derives the repository from the
 * `owner`/`repo` path segments, and the controller supplies `includeTemplates: true`, so the
 * client only forwards the persisted-query id as the `queryId` search param.
 *
 * Returns the `{repository}` root. A `null` repository is a valid response (repo not found or
 * no access) and is surfaced to the caller rather than thrown; only a missing `data` envelope
 * throws.
 */
export async function fetchDialogEntry(
  owner: string,
  repo: string,
  signal?: AbortSignal,
): Promise<CreateIssueDialogEntryQueryQuery> {
  const pathname = `/${encodeURIComponent(owner)}/${encodeURIComponent(repo)}/issues/new/dialog_entry`
  const response = await verifiedFetchJSON(`${pathname}?queryId=${CreateIssueDialogEntryQuery}`, {signal})

  if (!response.ok) {
    throw new Error(`CreateIssueDialogEntryQuery fetch failed (${response.status})`)
  }

  const json = (await response.json()) as DialogEntryResponse
  if (!json.data) {
    throw new Error('CreateIssueDialogEntryQuery returned no data')
  }

  return json.data
}

type LatestRequest = {current: number}

/**
 * Runs `request` as the newest request tracked by `latestRequest`, applying its result only if
 * no newer request has started in the meantime. Both a stale success and a stale failure are
 * suppressed, so a slow earlier request can never clobber a newer one (TanStack does not order
 * resolution across different query keys).
 */
export async function runLatestRequest<T>(
  latestRequest: LatestRequest,
  request: () => Promise<T>,
  applyResult: (result: T) => void,
  reportError: (error: unknown) => void,
): Promise<void> {
  const requestId = ++latestRequest.current

  try {
    const result = await request()
    if (requestId === latestRequest.current) {
      applyResult(result)
    }
  } catch (error) {
    if (requestId === latestRequest.current) {
      reportError(error)
    }
  }
}
